import { useState } from 'react';
import { genres, regions, years } from '@/mocks/dramas';
import type { SortKey } from '@/utils/drama';

export interface FilterState {
  genre: string;
  region: string;
  year: number | '';
  sort: SortKey;
}

interface FilterBarProps {
  filter: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
  resultCount: number;
}

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'update', label: '按更新时间' },
  { key: 'new', label: '新剧优先' },
  { key: 'hot', label: '按热度排序' },
];

export default function FilterBar({ filter, onChange, onReset, resultCount }: FilterBarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const setField = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    onChange({ ...filter, [key]: value });
  };

  const pill = (active: boolean) =>
    `whitespace-nowrap rounded-full border px-3 py-1 text-xs transition-colors cursor-pointer ${
      active
        ? 'border-primary-500 bg-primary-500 text-background-50'
        : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300'
    }`;

  const renderGroups = () => (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 md:flex-row md:items-start">
        <span className="w-14 shrink-0 pt-1 text-sm font-medium text-foreground-700">类型</span>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={pill(filter.genre === '')} onClick={() => setField('genre', '')}>
            全部
          </button>
          {genres.map((g) => (
            <button key={g} type="button" className={pill(filter.genre === g)} onClick={() => setField('genre', g)}>
              {g}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2 md:flex-row md:items-start">
        <span className="w-14 shrink-0 pt-1 text-sm font-medium text-foreground-700">地区</span>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={pill(filter.region === '')} onClick={() => setField('region', '')}>
            全部
          </button>
          {regions.map((r) => (
            <button key={r} type="button" className={pill(filter.region === r)} onClick={() => setField('region', r)}>
              {r}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2 md:flex-row md:items-start">
        <span className="w-14 shrink-0 pt-1 text-sm font-medium text-foreground-700">年份</span>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={pill(filter.year === '')} onClick={() => setField('year', '')}>
            全部
          </button>
          {years.map((y) => (
            <button key={y} type="button" className={pill(filter.year === y)} onClick={() => setField('year', y)}>
              {y} 年
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2 md:flex-row md:items-start">
        <span className="w-14 shrink-0 pt-1 text-sm font-medium text-foreground-700">排序</span>
        <div className="flex flex-wrap gap-2">
          {sortOptions.map((o) => (
            <button key={o.key} type="button" className={pill(filter.sort === o.key)} onClick={() => setField('sort', o.key)}>
              {o.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="mb-3 hidden items-center justify-between md:flex">
        <h2 className="font-heading text-base font-semibold text-foreground-950">筛选与排序</h2>
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-600 cursor-pointer"
        >
          <i className="ri-refresh-line"></i>
          重置条件
        </button>
      </div>

      <div className="hidden md:block">{renderGroups()}</div>

      <div className="flex items-center justify-between md:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-800 cursor-pointer"
        >
          <i className="ri-filter-3-line"></i>
          筛选条件
        </button>
        <span className="text-xs text-foreground-600">共 {resultCount} 部</span>
      </div>

      {mobileOpen && (
        <div className="mt-4 border-t border-background-200 pt-4 md:hidden animate-soft-in">
          {renderGroups()}
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={onReset}
              className="flex-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700 cursor-pointer"
            >
              重置
            </button>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="flex-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 cursor-pointer"
            >
              查看结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}